import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ArrowRight, KeyRound } from "lucide-react";
import { Skeleton, Tabs, TabsContent, TabsList, TabsTrigger, buttonVariants } from "@atrix-analytics/ui";
import { errorMessage } from "../api/http";
import { keysQuery } from "../api/queries";
import { ENVIRONMENTS, type EnvironmentName } from "../api/schemas";
import { CodeBlock } from "../components/code-block";
import { CopyField } from "../components/copy-button";
import { Card, CardHeader, PageHeader } from "../components/page";
import { WEB_INSTALL, webSnippet } from "../lib/snippets";
import { useWorkspace } from "../lib/workspace";

const ENV_COPY: Record<EnvironmentName, { label: string; note: string }> = {
  production: { label: "Production", note: "Your live app. This is the data your dashboards and billing count." },
  preview: { label: "Preview", note: "Staging and preview deploys, kept apart from production numbers." },
  development: { label: "Development", note: "Local builds. Use it freely — it never pollutes production." },
};

export function InstallPage() {
  const { project, environment, setEnvironment } = useWorkspace();
  const keys = useQuery(keysQuery(project.id));
  const activeKey = (env: EnvironmentName) => keys.data?.find((k) => k.environment === env && !k.revoked_at);

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-6">
      <PageHeader
        title="Install the SDK"
        description={`Send events from your app to ${project.name}. Each environment has its own public key, so test traffic never mixes with production.`}
      />

      <Card aria-labelledby="keys-h">
        <CardHeader
          id="keys-h"
          title="Public keys"
          description="Write-only keys that are safe to embed in web and mobile apps. They can send events but can't read anything."
        />
        <div className="flex flex-col divide-y divide-border">
          {keys.isPending &&
            ENVIRONMENTS.map((e) => (
              <div key={e} className="flex flex-col gap-2 px-4 py-3.5 sm:px-5">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-9 w-full" />
              </div>
            ))}
          {keys.isError && (
            <p role="alert" className="px-5 py-4 text-sm text-danger">
              Couldn't load keys. {errorMessage(keys.error)}{" "}
              <button type="button" className="underline" onClick={() => keys.refetch()}>
                Retry
              </button>
            </p>
          )}
          {keys.data &&
            ENVIRONMENTS.map((env) => {
              const k = activeKey(env);
              return (
                <div key={env} className="grid gap-2 px-4 py-3.5 sm:grid-cols-[9rem_minmax(0,1fr)] sm:items-center sm:px-5">
                  <div>
                    <p className="text-sm font-medium">{ENV_COPY[env].label}</p>
                    <p className="text-xs text-fg-muted sm:hidden">{ENV_COPY[env].note}</p>
                  </div>
                  {k ? (
                    <CopyField value={k.key} label={`${ENV_COPY[env].label} key`} />
                  ) : (
                    <p className="text-sm text-fg-muted">
                      No active key.{" "}
                      <Link
                        to="/p/$projectId/settings/project"
                        params={{ projectId: project.id }}
                        className="font-medium text-accent hover:underline"
                      >
                        Manage keys
                      </Link>
                    </p>
                  )}
                </div>
              );
            })}
        </div>
      </Card>

      <Card aria-labelledby="web-h">
        <CardHeader id="web-h" title="Web" description="Works with any bundler and framework. Mobile and server SDKs are coming next." />
        <div className="flex flex-col gap-4 p-4 sm:p-5">
          <Tabs value={environment} onValueChange={(v) => setEnvironment(v as EnvironmentName)}>
            <TabsList aria-label="Environment for the snippet" className="max-w-full overflow-x-auto">
              {ENVIRONMENTS.map((e) => (
                <TabsTrigger key={e} value={e}>
                  {ENV_COPY[e].label}
                </TabsTrigger>
              ))}
            </TabsList>
            {ENVIRONMENTS.map((env) => {
              const k = activeKey(env);
              return (
                <TabsContent key={env} value={env} className="flex flex-col gap-3">
                  <p className="text-sm text-fg-muted">{ENV_COPY[env].note}</p>
                  <ol className="flex flex-col gap-3">
                    <li className="flex flex-col gap-1.5">
                      <span className="text-xs font-medium text-fg-muted">1. Install</span>
                      <CodeBlock code={WEB_INSTALL} label="Terminal" />
                    </li>
                    <li className="flex flex-col gap-1.5">
                      <span className="text-xs font-medium text-fg-muted">2. Initialise and send an event</span>
                      {keys.isPending ? (
                        <Skeleton className="h-56 w-full" />
                      ) : (
                        <CodeBlock code={webSnippet(k?.key ?? "atx_pk_…")} label="app.ts" />
                      )}
                    </li>
                  </ol>
                </TabsContent>
              );
            })}
          </Tabs>
        </div>
      </Card>

      <div className="flex flex-col gap-3 rounded-lg border border-border bg-bg-raised p-4 sm:flex-row sm:items-center sm:p-5">
        <KeyRound aria-hidden className="size-5 shrink-0 text-fg-faint" />
        <p className="flex-1 text-sm text-fg-muted">
          Events show up in Insights within seconds of arriving. Rotate or revoke keys anytime in project settings.
        </p>
        <Link
          to="/p/$projectId/insights/trends"
          params={{ projectId: project.id }}
          className={buttonVariants({ variant: "primary" })}
        >
          Open Trends <ArrowRight aria-hidden />
        </Link>
      </div>
    </div>
  );
}
